import React, { useState } from 'react';
// Native MUI Components
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { Plus } from 'lucide-react';
// Native MUI Dialog
import { MuiDialog } from '@/components/ui';
import { SkiPass } from '@/types';
import { useI18n } from '@/hooks/useI18n';

interface AddSkiPassFormProps {
  onAddSkiPass?: (skiPass: Omit<SkiPass, 'id'>) => void;
  onUpdateSkiPass?: (skiPass: SkiPass) => void;
  editingSkiPass?: SkiPass;
  trigger?: React.ReactNode;
}

const AddSkiPassForm: React.FC<AddSkiPassFormProps> = ({ 
  onAddSkiPass, 
  onUpdateSkiPass, 
  editingSkiPass,
  trigger 
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: editingSkiPass?.name || '',
    type: editingSkiPass?.type || 'daily',
    validDays: editingSkiPass?.validDays || 1,
    adultPrice: editingSkiPass?.adultPrice || 0,
    youthPrice: editingSkiPass?.youthPrice || 0,
    seniorPrice: editingSkiPass?.seniorPrice || 0,
    childPrice: editingSkiPass?.childPrice || 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || formData.adultPrice <= 0) {
      return;
    }

    if (editingSkiPass && onUpdateSkiPass) {
      onUpdateSkiPass({
        ...editingSkiPass,
        ...formData,
        type: formData.type as 'daily' | 'multi-day' | 'season'
      });
    } else if (onAddSkiPass) {
      onAddSkiPass({
        ...formData,
        type: formData.type as 'daily' | 'multi-day' | 'season'
      });
    }

    // Reset form
    setFormData({
      name: '',
      type: 'daily',
      validDays: 1,
      adultPrice: 0,
      youthPrice: 0,
      seniorPrice: 0,
      childPrice: 0,
    });
    setOpen(false);
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      {trigger ? (
        <Box onClick={() => setOpen(true)}>
          {trigger}
        </Box>
      ) : (
        <Button 
          variant="contained" 
          onClick={() => setOpen(true)}
          startIcon={<Plus />}
          sx={{ bgcolor: 'primary.main' }}
        >
          {t('skipass.form.addSkiPass')}
        </Button>
      )}
      
      <MuiDialog
        open={open}
        onClose={() => setOpen(false)}
        title={editingSkiPass ? t('skipass.form.editSkiPass') : t('skipass.form.addNewSkiPass')}
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label={t('skipass.form.name')}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('skipass.form.namePlaceholder')}
              required
            />

            <FormControl fullWidth>
              <InputLabel>{t('skipass.form.type')} *</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                label={`${t('skipass.form.type')} *`}
              >
                <MenuItem value="daily">{t('skipass.form.typeDaily')}</MenuItem>
                <MenuItem value="multi-day">{t('skipass.form.typeMultiDay')}</MenuItem>
                <MenuItem value="season">{t('skipass.form.typeSeason')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={t('skipass.form.validDays')}
              type="number"
              value={formData.validDays}
              onChange={(e) => handleChange('validDays', parseInt(e.target.value))}
              inputProps={{ min: 1 }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label={t('skipass.form.adultPrice')}
                type="number"
                value={formData.adultPrice}
                onChange={(e) => handleChange('adultPrice', parseFloat(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
                required
              />

              <TextField
                fullWidth
                label={t('skipass.form.youthPrice')}
                type="number"
                value={formData.youthPrice}
                onChange={(e) => handleChange('youthPrice', parseFloat(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
              />

              <TextField
                fullWidth
                label={t('skipass.form.seniorPrice')}
                type="number"
                value={formData.seniorPrice}
                onChange={(e) => handleChange('seniorPrice', parseFloat(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
              />

              <TextField
                fullWidth
                label={t('skipass.form.childPrice')}
                type="number"
                value={formData.childPrice}
                onChange={(e) => handleChange('childPrice', parseFloat(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={() => setOpen(false)}>
              {t('skipass.form.cancel')}
            </Button>
            <Button type="submit" variant="contained">
              {editingSkiPass ? t('skipass.form.update') : t('skipass.form.add')} {t('skipass.skiPass')}
            </Button>
          </Box>
        </Box>
      </MuiDialog>
    </>
  );
};

export default AddSkiPassForm;
